import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
// import { AngularFireAuth } from '@angular/fire/compat/auth'; // Remove direct import
// import firebase from 'firebase/app';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { AuthService } from './auth.service'; // Import AuthService (assuming path)

@Injectable({
    providedIn: 'root'
})
export class UserTrackingService {
    private userId: string | null = null;
    private sessionId: string;
    private sessionStartTime: number;
    private lastActionTime: number;

    constructor(
        private afs: AngularFirestore,
        // private auth: AngularFireAuth
        private authService: AuthService // Inject AuthService
    ) {
        // Generate a unique session ID for this session
        this.sessionId = this.generateUniqueId();
        this.sessionStartTime = Date.now();
        this.lastActionTime = this.sessionStartTime;

        // Get the current user ID if authenticated
        // this.auth.user.subscribe(user => { // Use authService
        this.authService.authState.subscribe(user => {
            this.userId = user ? user.uid : null;

            // Log session start
            this.trackEvent('session_start', {
                browser: navigator.userAgent,
                screenSize: {
                    width: window.screen.width,
                    height: window.screen.height
                },
                viewportSize: {
                    width: window.innerWidth,
                    height: window.innerHeight
                }
            });
        });

        // Track when the user leaves the page
        window.addEventListener('beforeunload', () => {
            this.trackEvent('session_end', {
                sessionDuration: Date.now() - this.sessionStartTime
            });
        });
    }

    /**
     * Track a user event
     * @param eventName Name of the event
     * @param eventData Additional data for the event
     */
    trackEvent(eventName: string, eventData: any = {}) {
        return; // <-- Add this line to disable tracking

        // Don't track if no user is logged in and it's not a session event
        if (!this.userId && !eventName.startsWith('session_')) {
            return;
        }

        const now = Date.now();
        const timeSinceLastAction = now - this.lastActionTime;
        this.lastActionTime = now;

        // Deep clone and process eventData to stringify all nested objects/arrays
        const processedEventData = JSON.parse(JSON.stringify(eventData), (key, value) => {
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                // Keep top-level object, stringify nested ones
                // Check if this is the root object being processed by comparing with the original
                // This check is simplified; a more robust check might be needed for complex cases
                // However, for typical event data, this should prevent stringifying the main eventData object
                if (value !== eventData) {
                    return JSON.stringify(value);
                }
            } else if (Array.isArray(value)) {
                // Always stringify arrays to be safe
                return JSON.stringify(value);
            }
            return value; // Return primitives and null as is
        });


        const eventRecord = {
            userId: this.userId || 'anonymous',
            sessionId: this.sessionId,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            clientTimestamp: now,
            eventName,
            timeSinceLastAction,
            ...processedEventData // Spread the fully processed data
        };

        // Add to analytics collection
        this.afs.collection('analytics').add(eventRecord)
            .catch(error => console.error('Error tracking event:', error));
    }

    /**
     * Track model creation
     */
    trackModelCreation(modelType: string, dimensions: any) {
        this.trackEvent('model_created', {
            modelType,
            dimensions
        });
    }

    /**
     * Track when a primitive is added
     */
    trackPrimitiveAdded(primitiveType: string) {
        this.trackEvent('primitive_added', {
            primitiveType
        });
    }

    /**
     * Track sheet changes
     */
    trackSheetChange(changeType: string, rowCount: number, colCount: number) {
        this.trackEvent('sheet_changed', {
            changeType,
            rowCount,
            colCount
        });
    }

    /**
     * Track camera movements
     */
    trackCameraMovement(movementType: string, duration: number) {
        this.trackEvent('camera_movement', {
            movementType,
            duration
        });
    }

    /**
     * Track search operations
     */
    trackSearch(query: string, resultCount: number) {
        this.trackEvent('search_performed', {
            query,
            resultCount
        });
    }

    /**
     * Track model export
     */
    trackExport(exportType: string, objectCount: number) {
        this.trackEvent('model_exported', {
            exportType,
            objectCount
        });
    }

    /**
     * Track when AI features are used
     */
    trackAIUsage(aiFeature: string, promptLength: number) {
        this.trackEvent('ai_used', {
            aiFeature,
            promptLength
        });
    }

    /**
     * Track render performance
     */
    trackRenderPerformance(fps: number, objectCount: number, complexity: string) {
        this.trackEvent('render_performance', {
            fps,
            objectCount,
            complexity
        });
    }

    /**
     * Generate a unique ID for session tracking
     */
    private generateUniqueId(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
} 